import { PrimaryButton, SecondaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body3, H2 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { routes } from '@/modules/Router';
import { useForgotPassword } from '@/modules/Users/Users.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { forgotPasswordSchema, ForgotPasswordSchema } from './Users.schema';

export function ForgotPasswordForm() {
  const { register, handleSubmit, formState } = useForm<ForgotPasswordSchema>({
    mode: 'onBlur',
    resolver: zodResolver(forgotPasswordSchema),
  });
  const mutation = useForgotPassword();
  const navigate = useNavigate();

  useEffect(() => {
    if (mutation.status === 'success') {
      toast.success(mutation.data.data.message || 'Check your inbox for your reset password link.', {
        autoClose: 5000,
      });
      navigate(routes.login);
    }
  }, [mutation.data?.data.message, mutation.status, navigate]);

  useEffect(() => {
    if (mutation.status === 'error') {
      toast.error('Failed to send email. Please try again later.', {
        autoClose: 3000,
      });
      console.error('Submitting Forgot Password failed. Error message:', mutation.error);
    }
  }, [mutation.error, mutation.status]);

  const onSubmit = (data: ForgotPasswordSchema) => {
    mutation.mutate(data);
  };

  const onCancel = () => {
    navigate(routes.login);
  };

  return (
    <div className={cn('h-full flex items-center mt-10')}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col gap-6 w-fit shadow rounded-lg p-20')}>
        <H2 className={cn('text-center')}>Forgot Password?</H2>
        <Body3 className={cn('text-neutral-500 w-full text-center')}>
          Enter your email address to reset password. A link will be sent to you via email.
        </Body3>
        <div>
          <label htmlFor="email" className={cn('block text-sm font-medium text-gray-700')}>
            Email Address
          </label>
          <input
            {...register('email')}
            type="text"
            id="email"
            className={cn(
              'mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm'
            )}
            placeholder="Enter your email"
          />
          <div className={cn('h-4')}>
            {formState.errors.email && (
              <div className={cn('text-error-500 bg-error-50 border-error-200 rounded px-2 py-1 mt-0 mx-2 text-sm')}>
                {formState.errors.email?.message}
              </div>
            )}
          </div>
        </div>
        <div className={cn('flex gap-6')}>
          <SecondaryButton onClick={onCancel} disabled={mutation.status === 'pending'}>
            Back to Login
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={mutation.status === 'pending'}>
            {mutation.status === 'pending' ? (
              <div className={cn('w-full flex justify-center items-center')}>
                <Loading size={'small'} inline />
              </div>
            ) : (
              'Send Email'
            )}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
